import React, { useState, useEffect, useRef } from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    TextField,
    Autocomplete,
    Checkbox,
    FormControlLabel,
    MenuItem
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { RoleMapping } from "../constants/roles";
import { formatPhoneNumber } from "../utils/phoneUtils";
import { useRefresh } from "../context/RefreshContext";
import { formatName } from "../utils/nameUtils";
import VirtualizedListbox from "../components/VirtualizedListbox";

const AddModal = ({ open, onClose, initialCategory, preselectedCustomer, preselectedPet }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({});
    const [customers, setCustomers] = useState([]); // List of customers for searchable dropdown
    //const [customerSearch, setCustomerSearch] = useState("");
    const [pets, setPets] = useState([]); // List of pets for searchable dropdown
    const [groomers, setGroomers] = useState([]); // List of groomers for searchable dropdown
    const [selectedCustomer, setSelectedCustomer] = useState(preselectedCustomer || null);
    const [selectedGroomer, setSelectedGroomer] = useState(null);
    const firstNameRef = useRef(null);
    const petNameRef = useRef(null);
    const userNameRef = useRef(null);
    const { triggerRefresh } = useRefresh();
    const [inputValue, setInputValue] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isFormValid = () => {
        if (initialCategory === "Customer") {
            return formData.firstName && formData.lastName;
        }
        if (initialCategory === "Pet") {
            return formData.customerId && formData.name;
        }
        if (initialCategory === "Service") {
            return formData.petId && formData.date && formData.time;
        }
        if (initialCategory === "Groomer") {
            return formData.firstName && formData.lastName;
        }
        if (initialCategory === "User") {
            return formData.name && formData.username && formData.password;
        }
        return true;
    };

    useEffect(() => {
        if (open) {
            if (initialCategory === "Service") setFormData({ date: "", time: "" });
            else setFormData({});
            setTimeout(() => {
                if (initialCategory === "Customer" || initialCategory === "Groomer") {
                    firstNameRef.current?.focus();
                } else if (initialCategory === "Pet" && preselectedCustomer) {
                    petNameRef.current?.focus();
                } else if (initialCategory === "User") {
                    userNameRef.current?.focus();
                }
            }, 100); // Small delay to ensure modal renders first
        }
    }, [open, initialCategory, preselectedCustomer]);

    // Handle form input changes
    const handleInputChange = (event) => {
        const { name, value } = event.target;
        let formattedValue = value;

        // Apply phone number formatting if the field is phone-related
        if (name.includes("phone")) {
            formattedValue = formatPhoneNumber(value);
        }
        setFormData((prev) => ({ ...prev, [name]: formattedValue }));
    };

    // Preselect customerId if coming from Customer Details page
    useEffect(() => {
        if (preselectedCustomer) {
            setSelectedCustomer(preselectedCustomer);
            setFormData((prev) => ({ ...prev, customerId: preselectedCustomer.id }));
            setInputValue(`${preselectedCustomer.firstName} ${preselectedCustomer.lastName}`);
        }
    }, [preselectedCustomer]);

    // Preselect petId if coming from Pet Details page
    useEffect(() => {
        if (preselectedPet) {
            setFormData((prev) => ({ ...prev, petId: preselectedPet.id }));
        }
    }, [preselectedPet]);

    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const token = localStorage.getItem("token");

                const response = await api.get("/customers", {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        search: undefined,
                        limit: 10000
                    },
                });

                setCustomers(response.data.data);
            } catch (error) {
                console.error("Error fetching customers:", error);
            }
        };

        if (initialCategory === "Pet" || initialCategory === "Service") {
            fetchCustomers();
        }
    }, [initialCategory]);

    const filteredCustomers = customers.filter((cust) =>
        `${cust.firstName} ${cust.lastName}`.toLowerCase().includes(inputValue.toLowerCase())
    );

    useEffect(() => {
        const fetchPets = async () => {
            if (!formData.customerId) return;
            try {
                const token = localStorage.getItem("token");
                const response = await api.get(
                    `/customers/${formData.customerId}/pets`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setPets(response.data.data);
            } catch (error) {
                console.error("Error fetching pets:", error);
            }
        };

        if (formData.customerId) {
            fetchPets();
        }
    }, [formData.customerId]);

    // Fetch groomers when modal opens
    useEffect(() => {
        const fetchGroomers = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await api.get("/groomers/public", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                //console.log("Groomers response data:", response.data);
                const groomerList = response.data.filter((g) => g.active);
                setGroomers(groomerList);

                // If there's only 1 groomer, preselect it
                if (groomerList.length === 1) {
                    setSelectedGroomer(groomerList[0]);
                    setFormData((prev) => ({ ...prev, groomerId: groomerList[0].id }));
                }
            } catch (error) {
                console.error("Error fetching groomers:", error);
            }
        };

        if (initialCategory === "Service") {
            fetchGroomers();
        }
    }, [initialCategory]);

    // Handle form submission
    const handleSubmit = async () => {
        if (isSubmitting) return; // Avoid duplicate submits
        setIsSubmitting(true);
        try {
            const token = localStorage.getItem("token");
            const payload = { ...formData }

            if (initialCategory === "Customer") {
                delete payload.customerId;
                delete payload.petId;
                delete payload.medissues;
                if (!payload.address) payload.address = "";
                if (!payload.phone2) payload.phone2 = "";
                if (!payload.email) payload.email = "";
            }

            if (initialCategory === "Pet") {
                delete payload.petId;
                if (!payload.breed) payload.breed = "";
                if (!payload.sex) payload.sex = "";
                if (!payload.weight) payload.weight = "";
                if (!payload.color) payload.color = "";
                if (!payload.age) payload.age = "";
                if (!payload.medissues) payload.medissues = "";
            }

            if (!payload.comments) payload.comments = "";
    
            if (initialCategory === "Service") {
                payload.date = payload.date ? new Date(payload.date).toISOString().split("T")[0] : "";
                if (payload.time instanceof Date) {
                    const hrs = payload.time.getHours().toString().padStart(2, '0');
                    const mins = payload.time.getMinutes().toString().padStart(2, '0');
                    payload.time = `${hrs}:${mins}`;
                }
                delete payload.customerId;
                delete payload.medissues;
            }
            if (initialCategory === "Groomer") {
                delete payload.customerId;
                delete payload.petId;
                delete payload.medissues;
                if (!payload.phone2) payload.phone2 = "";
                if (!payload.email) payload.email = "";
                payload.active = payload.active ?? true;
            }

            if (initialCategory === "User") {
                delete payload.medissues;
            }

            const response = await api.post(`/${initialCategory.toLowerCase()}s`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const newId = response.data.id;

            setFormData({});
            setInputValue("");
            setSelectedGroomer(null);

            if (initialCategory === "Customer") {
                navigate(`/customers/${newId}`);
            } else if (initialCategory === "Pet" || initialCategory === "Service") {
                triggerRefresh();
                navigate(`/customers/${formData.customerId}`);
            } else if (initialCategory === "Groomer") {
                navigate(`/groomers/${newId}`);
            } else if (initialCategory === "User") {
                navigate(`/users/${newId}`);
            }

            onClose();
        } catch (error) {
            console.error("Error creating record:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{ width: 400, bgcolor: "white", p: 3, mx: "auto", mt: 10, borderRadius: 2 }}>
                <Typography variant="h6">Add New {initialCategory}</Typography>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                        setTimeout(() => {
                            document.activeElement.blur();
                        }, 100);
                    }}
                >
                    {/* Customer Form */}
                    {initialCategory === "Customer" && (
                        <>
                            <TextField
                                label="First Name"
                                name="firstName"
                                fullWidth
                                sx={{ mt: 2 }}
                                value={formData.firstName || ""}
                                onChange={handleInputChange}
                                onBlur={(e) => {
                                    const formattedValue = formatName(e.target.value);
                                    setFormData((prev) => ({ ...prev, firstName: formattedValue }));
                                }}
                                inputRef={firstNameRef}
                            />
                            <TextField
                                label="Last Name"
                                name="lastName"
                                fullWidth
                                sx={{ mt: 2 }}
                                value={formData.lastName || ""}
                                onChange={handleInputChange}
                                onBlur={(e) => {
                                    const formattedValue = formatName(e.target.value);
                                    setFormData((prev) => ({ ...prev, lastName: formattedValue }));
                                }}
                            />
                            <TextField
                                label="Address"
                                name="address"
                                fullWidth
                                sx={{ mt: 2 }}
                                onChange={handleInputChange}
                            />
                            <TextField
                                label="Phone 1"
                                name="phone1"
                                fullWidth
                                sx={{ mt: 2 }}
                                value={formData.phone1 || ""}
                                onChange={handleInputChange}
                            />
                            <TextField
                                label="Phone 2"
                                name="phone2"
                                fullWidth
                                sx={{ mt: 2 }}
                                value={formData.phone2 || ""}
                                onChange={handleInputChange}
                            />
                            <TextField
                                label="Email"
                                name="email"
                                fullWidth
                                sx={{ mt: 2 }}
                                onChange={handleInputChange}
                            />
                            <TextField
                                label="Comments"
                                name="comments"
                                fullWidth
                                sx={{ mt: 2 }}
                                onChange={handleInputChange}
                            />
                        </>
                    )}

                    {/* Pet Form */}
                    {initialCategory === "Pet" && (
                        <>
                            <Autocomplete
                                options={filteredCustomers}
                                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                                inputValue={inputValue}
                                value={
                                    selectedCustomer ||
                                    filteredCustomers.find((c) => c.id === formData.customerId) ||
                                    null
                                }
                                disabled={!!preselectedCustomer}
                                onInputChange={(event, newInputValue) => {
                                    setInputValue(newInputValue);
                                }}
                                onChange={(event, newValue) => {
                                    setSelectedCustomer(newValue);
                                    setFormData((prev) => ({ ...prev, customerId: newValue?.id }));
                                }}
                                ListboxComponent={VirtualizedListbox}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select Customer" fullWidth sx={{ mt: 2 }} />
                                )}
                            />
                            <TextField
                                label="Pet Name"
                                name="name"
                                fullWidth
                                sx={{ mt: 2 }}
                                value={formData.name || ""}
                                onChange={handleInputChange}
                                onBlur={(e) => {
                                    const formattedValue = formatName(e.target.value);
                                    setFormData((prev) => ({ ...prev, name: formattedValue }));
                                }}
                                inputRef={petNameRef}
                            />
                            <TextField label="Breed" name="breed" fullWidth sx={{ mt: 2 }} onChange={handleInputChange} />
                            <TextField label="Sex" name="sex" fullWidth sx={{ mt: 2 }} onChange={handleInputChange} />
                            <TextField label="Weight" name="weight" fullWidth sx={{ mt: 2 }} onChange={handleInputChange} />
                            <TextField label="Color" name="color" fullWidth sx={{ mt: 2 }} onChange={handleInputChange} />
                            <TextField label="Age" name="age" fullWidth sx={{ mt: 2 }} onChange={handleInputChange} />
                            <TextField label="Medical Issues" name="medissues" fullWidth sx={{ mt: 2 }} onChange={handleInputChange} />
                            <TextField label="Comments" name="comments" fullWidth sx={{ mt: 2 }} onChange={handleInputChange} />
                        </>
                    )}

                    {/* Service Form */}
                    {initialCategory === "Service" && (
                        <>
                            <Autocomplete
                                options={filteredCustomers}
                                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                                inputValue={inputValue}
                                value={
                                    selectedCustomer ||
                                    filteredCustomers.find((c) => c.id === formData.customerId) ||
                                    null
                                }
                                disabled={!!preselectedCustomer}
                                onInputChange={(event, newInputValue) => {
                                    setInputValue(newInputValue);
                                }}
                                onChange={(event, newValue) => {
                                    setSelectedCustomer(newValue);
                                    setFormData((prev) => ({ ...prev, customerId: newValue?.id }));
                                }}
                                ListboxComponent={VirtualizedListbox}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select Customer" fullWidth sx={{ mt: 2 }} />
                                )}
                            />
                            <Autocomplete
                                options={pets}
                                getOptionLabel={(option) => option.name}
                                value={
                                    preselectedPet ||
                                    pets.find((p) => p.id === formData.petId) ||
                                    null
                                }
                                disabled={!!preselectedPet}
                                onChange={(event, newValue) =>
                                    setFormData((prev) => ({ ...prev, petId: newValue?.id }))
                                }
                                renderInput={(params) => (
                                    <TextField {...params} label="Select Pet" fullWidth sx={{ mt: 2 }} />
                                )}
                            />
                            <TextField
                                label="Date"
                                type="date"
                                name="date"
                                fullWidth
                                sx={{ mt: 2 }}
                                value={formData.date ?? ""}
                                onChange={e => setFormData({ ...formData, date: e.target.value })}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                            <TextField
                                label="Time"
                                type="time"
                                name="time"
                                placeholder="--:-- --"
                                fullWidth
                                sx={{ mt: 2 }}
                                value={formData.time ?? ""}
                                onChange={e => setFormData({ ...formData, time: e.target.value })}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                            <TextField
                                label="Service"
                                name="service"
                                fullWidth
                                sx={{ mt: 2 }}
                                value={formData.service || ""}
                                onChange={handleInputChange}
                                onBlur={(e) => {
                                    const formattedValue = formatName(e.target.value);
                                    setFormData((prev) => ({ ...prev, service: formattedValue }));
                                }}
                            />
                            <TextField label="Amount" name="amount" fullWidth sx={{ mt: 2 }} onChange={handleInputChange} />
                            <Autocomplete
                                options={groomers}
                                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                                value={selectedGroomer || null}
                                disabled={groomers.length === 1} // Disable if only one groomer
                                onChange={(event, newValue) => {
                                    setSelectedGroomer(newValue);
                                    setFormData((prev) => ({ ...prev, groomerId: newValue?.id }));
                                }}
                                renderInput={(params) => <TextField {...params} label="Select Groomer" fullWidth sx={{ mt: 2 }} />}
                            />
                            <TextField label="Comments" name="comments" fullWidth sx={{ mt: 2 }} onChange={handleInputChange} />
                        </>
                    )}

                    {/* Groomer Form */}
                    {initialCategory === "Groomer" && (
                        <>
                            <TextField
                                label="First Name"
                                name="firstName"
                                fullWidth
                                sx={{ mt: 2 }}
                                value={formData.firstName || ""}
                                onChange={handleInputChange}
                                onBlur={(e) => {
                                    const formattedValue = formatName(e.target.value);
                                    setFormData((prev) => ({ ...prev, firstName: formattedValue }));
                                }}
                                inputRef={firstNameRef}
                            />
                            <TextField
                                label="Last Name"
                                name="lastName"
                                fullWidth
                                sx={{ mt: 2 }}
                                value={formData.lastName || ""}
                                onChange={handleInputChange}
                                onBlur={(e) => {
                                    const formattedValue = formatName(e.target.value);
                                    setFormData((prev) => ({ ...prev, lastName: formattedValue }));
                                }}
                            />
                            <TextField label="Address" name="address" fullWidth sx={{ mt: 2 }} onChange={handleInputChange} />
                            <TextField
                                label="Phone 1"
                                name="phone1"
                                fullWidth
                                sx={{ mt: 2 }}
                                value={formData.phone1 || ""}
                                onChange={handleInputChange} // Keeps updating as user types
                            />
                            <TextField
                                label="Phone 2"
                                name="phone2"
                                fullWidth
                                sx={{ mt: 2 }}
                                value={formData.phone2 || ""}
                                onChange={handleInputChange} // Keeps updating as user types
                            />
                            <TextField label="Email" name="email" fullWidth sx={{ mt: 2 }} onChange={handleInputChange} />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.active ?? true} // Default to true if undefined
                                        onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                                    />
                                }
                                label="Active"
                            />
                            <TextField label="Comments" name="comments" fullWidth sx={{ mt: 2 }} onChange={handleInputChange} />
                        </>
                    )}

                    {/* User Form */}
                    {initialCategory === "User" && (
                        <>
                            <TextField
                                label="Name"
                                name="name"
                                fullWidth
                                sx={{ mt: 2 }}
                                value={formData.name || ""}
                                onChange={handleInputChange}
                                onBlur={(e) => {
                                    const formattedValue = formatName(e.target.value);
                                    setFormData((prev) => ({ ...prev, name: formattedValue }));
                                }}
                                inputRef={userNameRef}
                            />
                            <TextField label="Email" name="email" fullWidth sx={{ mt: 2 }} onChange={handleInputChange} />
                            <TextField
                                label="Phone 1"
                                name="phone"
                                fullWidth
                                sx={{ mt: 2 }}
                                value={formData.phone || ""}
                                onChange={handleInputChange} // Keeps updating as user types
                            />
                            <TextField label="Username" name="username" fullWidth sx={{ mt: 2 }} onChange={handleInputChange} />
                            <TextField label="Password" type="password" name="password" fullWidth sx={{ mt: 2 }} onChange={handleInputChange} />
                            <TextField
                                select
                                label="Role"
                                name="role"
                                value={formData.role ?? ""}
                                onChange={(e) => setFormData(prev => ({ ...prev, role: Number(e.target.value) }))}
                                fullWidth
                                sx={{ mt: 2 }}
                            >
                                {Object.entries(RoleMapping).map(([key, label]) => (
                                    <MenuItem key={key} value={Number(key)}>{label}</MenuItem>
                                ))}
                            </TextField>
                            <TextField label="Comments" name="comments" fullWidth sx={{ mt: 2 }} onChange={handleInputChange} />
                        </>
                    )}

                    {/* Buttons */}
                    <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
                        <Button onClick={onClose} color="error">Cancel</Button>
                        <Button
                            type="submit"
                            onClick={handleSubmit}
                            variant="contained"
                            color="primary"
                            disabled={!isFormValid() || isSubmitting}
                        >
                            Add {initialCategory}
                        </Button>
                    </Box>
                </form>
            </Box>
        </Modal>
    );
};

export default AddModal;